import { Box, Typography, Paper, Button, InputAdornment } from '@mui/material';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faEye } from '@fortawesome/free-regular-svg-icons';

import RenderIf from '../../renderIf';
import { MuiStyledInput } from '../../shared/muiControls';

import useChangePassword from './useChangePassword';

/**A component for changing the Password for another User in Cognito, used by Admin.
 * @param {string} userId The User ID related to the user in the database.
 * @param {Array} buttons An Array of React Elements (MUI Buttons) to display alongside the default save.
 * @param {function} callback A function to call to do any cleanup or necessary state changes once Password has been changed successfully.
 * @returns {ReactNode} A ReactNode that renders the Change Password view.
 */
const ChangePassword = ({ callback, userId, buttons }) => {
    const {
        isDoingAjax,
        newPassword,
        isNewPasswordError,
        confirmNewPassword,
        isConfirmNewPasswordError,
        isNewPasswordMasked,
        confirmNewPasswordErrorReason,
        isConfirmNewPasswordMasked,
        updateState,
        handleSubmit,
    } = useChangePassword(userId, callback);

    return (
        <Box>
            <Paper
                sx={(theme) => ({
                    padding: theme.spacing(5),
                })}
            >
                <Box>
                    <Typography
                        sx={(theme) => ({
                            fontSize: '2rem',
                            borderBottom: `${theme.spacing(1 / 16)} solid black`,
                            marginBottom: theme.spacing(3),
                        })}
                    >
                        Change password
                    </Typography>
                </Box>

                <Box
                    sx={(theme) => ({
                        mb: theme.spacing(3),
                    })}
                >
                    <Typography>
                        Please enter a new password. The new password must be different than the previous password, it must be at least 8
                        characters,it must contain at least one uppercase letter, one lowercase letter, one number, and one of the following
                        symbols: ~,!,@,#,$,%,^,&
                    </Typography>
                </Box>

                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(3),
                        mb: theme.spacing(3),
                    })}
                >
                    <MuiStyledInput
                        id="new-password"
                        label="New password"
                        type={isNewPasswordMasked ? 'password' : 'text'}
                        value={newPassword}
                        onChange={(event) => updateState('newPassword', event.target.value)}
                        error={isNewPasswordError}
                        disabled={isDoingAjax}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment
                                    sx={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={isNewPasswordMasked ? faEyeSlash : faEye}
                                        onClick={() => updateState('isNewPasswordMasked', !isNewPasswordMasked)}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <MuiStyledInput
                        id="confirm-new-password"
                        label="Confirm new password"
                        type={isConfirmNewPasswordMasked ? 'password' : 'text'}
                        value={confirmNewPassword}
                        onChange={(event) => updateState('confirmNewPassword', event.target.value)}
                        error={isConfirmNewPasswordError}
                        helperText={confirmNewPasswordErrorReason || null}
                        disabled={isDoingAjax}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment
                                    sx={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={isConfirmNewPasswordMasked ? faEyeSlash : faEye}
                                        onClick={() => updateState('isConfirmNewPasswordMasked', !isConfirmNewPasswordMasked)}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        gap: theme.spacing(2),
                        justifyContent: 'right',
                    })}
                >
                    <RenderIf isTrue={buttons}>
                        {buttons.map((button) => {
                            //Use the majority of the properties already defined in the array, but override/add the disabled prop
                            return React.cloneElement(button, {
                                disabled: isDoingAjax,
                            });
                        })}
                    </RenderIf>

                    <Button
                        variant="contained"
                        onClick={() => handleSubmit()}
                        disabled={isDoingAjax}
                    >
                        Change password
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default ChangePassword;
